import { Link } from 'react-router-dom';
import { BarChart3, CalendarClock } from 'lucide-react';
import {
  Avatar,
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  ProgressBar,
} from '@tutor/ui';
import { useStudents, useTutorDashboard } from '../../hooks/queries';
import { formatRelativeDayTime } from '../../lib/format';

/**
 * Assessment overview.
 *
 * Reads from the data the dashboard and roster already expose rather than a dedicated
 * endpoint — scheduling and grading assessments is the next slice of work, and this
 * gives the route something honest to show until then.
 */
export function AssessmentsPage() {
  const { data: dashboard, isLoading, isError } = useTutorDashboard();
  const { data: students } = useStudents({ page: 1 });

  if (isLoading) return <LoadingState />;
  if (isError || !dashboard) return <ErrorState description="We could not load assessments." />;

  return (
    <>
      <PageHeader title="Assessments" description="Scheduled assessments and where each student stands." />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Upcoming" />
          <CardBody>
            {dashboard.upcomingAssessments.length === 0 ? (
              <EmptyState
                title="Nothing scheduled"
                description="Assessments you schedule will appear here."
                icon={<CalendarClock className="h-5 w-5" aria-hidden />}
              />
            ) : (
              <ul className="space-y-3">
                {dashboard.upcomingAssessments.map((assessment) => (
                  <li key={assessment.id} className="rounded-lg border border-slate-100 p-3">
                    <p className="text-sm font-medium text-slate-900">{assessment.title}</p>
                    <p className="mt-0.5 text-sm text-slate-600">{assessment.studentName}</p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
                      <CalendarClock className="h-3 w-3" aria-hidden />
                      {formatRelativeDayTime(assessment.scheduledFor)}
                    </p>
                    {assessment.subjectName && (
                      <Badge tone="brand" className="mt-2">
                        {assessment.subjectName}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Attainment across your students" />
          <CardBody>
            {(students?.items.length ?? 0) === 0 ? (
              <EmptyState title="No students yet" icon={<BarChart3 className="h-5 w-5" aria-hidden />} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {students!.items.map((student) => (
                  <li key={student.id}>
                    <Link to={`/tutor/students/${student.id}`} className="flex items-center gap-3 py-3 hover:bg-slate-50">
                      <Avatar name={student.user.fullName} src={student.user.avatarUrl} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">{student.user.fullName}</p>
                        <p className="truncate text-xs text-slate-500">
                          {student.subjects.map((s) => s.name).join(', ') || '—'}
                        </p>
                      </div>
                      <div className="w-32 shrink-0">
                        {student.averageProgress !== null ? (
                          <ProgressBar value={student.averageProgress} size="sm" />
                        ) : (
                          <span className="text-xs text-slate-400">No data</span>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
